import React, { useState, useCallback } from 'react';
import { ProductoCreate, ProductoUpdate } from '../../types';
import { api } from '../../api/client';
import RichTextEditor from '../shared/RichTextEditor';

interface Props {
  initialData?: ProductoCreate;
  onSubmit: () => void;
  onCancel: () => void;
  isEdit?: boolean;
  id?: number;
}

export default function ProductoForm({
  initialData,
  onSubmit,
  onCancel,
  isEdit = false,
  id,
}: Props) {
  const [data, setData] = useState<ProductoCreate>(
    initialData || {
      nombre: '',
      descripcion: '',
      precio: 0,
      url_imagen: '',
      categoria: '',
      stock: 0,
    }
  );
  const [imagen, setImagen] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('nombre', data.nombre);
      formData.append('descripcion', data.descripcion || '');
      formData.append('precio', data.precio.toString());
      formData.append('categoria', data.categoria || '');
      formData.append('stock', data.stock.toString());
      if (imagen) formData.append('imagen', imagen);

      if (isEdit && id) {
        await api.productos.update(id, formData);
      } else {
        await api.productos.create(formData);
      }

      onSubmit();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Error al guardar el producto';
      setError(errorMessage);
      console.error('Error al enviar producto:', err);
    } finally {
      setLoading(false);
    }
  }, [data, imagen, isEdit, id, onSubmit]);

  return (
    <form onSubmit={handleSubmit} className="bg-white p-4 rounded shadow mb-4">
      {error && (
        <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded" role="alert">
          <strong>Error:</strong> {error}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <input
          type="text"
          className="border p-2 w-full rounded"
          placeholder="Nombre *"
          value={data.nombre}
          onChange={(e) => setData({ ...data, nombre: e.target.value })}
          required
          disabled={loading}
        />
        <input
          type="number"
          step="0.01"
          className="border p-2 w-full rounded"
          placeholder="Precio *"
          value={data.precio || ''}
          onChange={(e) => setData({ ...data, precio: parseFloat(e.target.value) || 0 })}
          required
          disabled={loading}
        />
        <input
          type="text"
          className="border p-2 w-full rounded"
          placeholder="Categoría"
          value={data.categoria || ''}
          onChange={(e) => setData({ ...data, categoria: e.target.value })}
          disabled={loading}
        />
        <input
          type="number"
          className="border p-2 w-full rounded"
          placeholder="Stock"
          value={data.stock || ''}
          onChange={(e) => setData({ ...data, stock: parseInt(e.target.value) || 0 })}
          disabled={loading}
        />
        <div className="col-span-1 md:col-span-2">
          <label className="block mb-1 font-medium">Imagen</label>
          <input
            type="file"
            accept="image/*"
            onChange={(e) => setImagen(e.target.files?.[0] || null)}
            className="w-full"
            disabled={loading}
          />
          {!imagen && data.url_imagen && (
            <img
              src={data.url_imagen}
              alt="Vista previa"
              className="mt-2 max-h-40 border rounded"
            />
          )}
        </div>
        <div className="col-span-1 md:col-span-2">
          <label className="block mb-1 font-medium">Descripción *</label>
          <RichTextEditor
            value={data.descripcion || ''}
            onChange={(html) => setData({ ...data, descripcion: html })}
            placeholder="Descripción del producto..."
            minHeight="150px"
          />
        </div>
      </div>
      <div className="mt-4 flex space-x-2">
        <button
          type="submit"
          className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:bg-blue-300 disabled:cursor-not-allowed"
          disabled={loading}
        >
          {loading ? 'Guardando...' : isEdit ? 'Actualizar' : 'Crear'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600 disabled:bg-gray-300"
          disabled={loading}
        >
          Cancelar
        </button>
      </div>
    </form>
  );
}
